"use strict";
/**
 * Pure utility for auto-matching response fields to target columns.
 * No UI, no framework dependencies — reusable across frontend and backend.
 *
 * Type compatibility is a HARD FILTER: incompatible pairs are excluded
 * entirely (e.g. attachment → number). Within compatible/castable pairs,
 * name similarity determines the ranking.
 *
 * Confidence:
 *   high   — name match + compatible/castable type
 *   medium — fuzzy name OR type-only match (compatible/castable, no name)
 *   low    — weak name overlap with castable type
 *
 * Each suggestion carries `alternatives` — all type-compatible candidates
 * for that target, sorted by score desc, for the dropdown.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.rateFieldMatch = rateFieldMatch;
exports.autoMatchFields = autoMatchFields;
exports.rateTableMatch = rateTableMatch;
const search_extract_1 = require("@streamlain/search-extract");
const index_1 = require("./index");
// =============================================================================
// Normalization
// =============================================================================
function normalize(s) {
    return s.toLowerCase().replace(/[._\-\s]/g, '');
}
function tokenize(s) {
    return s
        .toLowerCase()
        .split(/[._\-\s]+/)
        .filter(Boolean);
}
// =============================================================================
// Scoring
// =============================================================================
/** Name-based score: 0 (no name similarity) to 1 (perfect). */
function nameScore(responseName, columnName) {
    const normResponse = normalize(responseName);
    const normColumn = normalize(columnName);
    if (normResponse === normColumn) {
        return responseName.trim() === columnName.trim() ? 1.0 : 0.95;
    }
    const dist = (0, search_extract_1.editDistance)(normResponse, normColumn);
    const maxLen = Math.max(normResponse.length, normColumn.length);
    // Length-normalized similarity: short strings need proportionally small dist.
    // "file" vs "title" (dist=2, maxLen=5, sim=0.6) → no auto-match.
    // Long typos like "supplier_adress" → "supplier_address" (sim=0.94) still pass.
    const similarity = 1 - dist / maxLen;
    if (similarity >= 0.7)
        return similarity;
    if (normResponse.includes(normColumn) || normColumn.includes(normResponse)) {
        const ratio = Math.min(normResponse.length, normColumn.length) /
            Math.max(normResponse.length, normColumn.length);
        return 0.5 + ratio * 0.3;
    }
    const responseTokens = tokenize(responseName);
    const columnTokens = tokenize(columnName);
    if (columnTokens.length === 0 || responseTokens.length === 0)
        return 0;
    let matchedTokens = 0;
    for (const ct of columnTokens) {
        if (responseTokens.some(rt => rt === ct))
            matchedTokens++;
    }
    if (matchedTokens === 0)
        return 0;
    return (matchedTokens / columnTokens.length) * 0.5;
}
function autoMapCastTier(sourceType, targetType) {
    const compat = (0, index_1.checkCompatibility)(targetType, sourceType);
    if (compat === 'compatible')
        return 'compatible';
    if (compat === 'incompatible')
        return 'excluded';
    // compat === 'castable' — decide tier
    const s = (0, index_1.normalizeType)(sourceType);
    const t = (0, index_1.normalizeType)(targetType);
    // Tier 1: natural casts (no data loss)
    if (t === 'text' &&
        ['select', 'multi-select', 'number', 'date', 'checkbox'].includes(s))
        return 'natural';
    if (s === 'text' && t === 'rich-text')
        return 'natural';
    if ((s === 'select' && t === 'multi-select') ||
        (s === 'multi-select' && t === 'select'))
        return 'natural';
    if (t === 'json' || s === 'json')
        return 'natural';
    // Tier 2: lossy / risky casts
    if (s === 'rich-text' && t === 'text')
        return 'lossy'; // markup lost
    if (s === 'attachment' && t === 'text')
        return 'lossy'; // only filename
    if (s === 'relation' && t === 'text')
        return 'lossy'; // relation ID, not display name
    if (s === 'text' && t === 'relation')
        return 'lossy'; // high-risk, needs correct config
    // Everything else castable but not auto-map-safe
    return 'excluded';
}
/**
 * Combined score for a source→target pair.
 *
 * With name match:
 *   compatible → full name score (high)
 *   natural    → name score - 0.05 (high/medium)
 *   lossy      → name score - 0.15 (medium)
 *   excluded   → 0
 *
 * Without name match:
 *   compatible → 0.45 (medium)
 *   natural    → 0.3 (low — visible in dropdown)
 *   lossy      → 0.15 (visible in dropdown, not auto-assigned)
 *   excluded   → 0
 */
function pairScore(field, col) {
    const tier = autoMapCastTier(field.type, col.type);
    if (tier === 'excluded')
        return 0;
    const pathS = nameScore(field.path, col.name);
    const dispS = field.displayName ? nameScore(field.displayName, col.name) : 0;
    const best = Math.max(pathS, dispS);
    if (best > 0) {
        switch (tier) {
            case 'compatible':
                return Math.max(best, 0.45);
            case 'natural':
                return Math.max(best - 0.05, 0.4);
            case 'lossy':
                return Math.min(Math.max(best - 0.15, 0.25), 0.75);
        }
    }
    // No name match — type-only
    switch (tier) {
        case 'compatible':
            return 0.45;
        case 'natural':
            return 0.3;
        case 'lossy':
            return 0.15;
    }
}
function scoreToConfidence(score) {
    if (score >= 0.8)
        return 'high';
    if (score >= 0.4)
        return 'medium';
    return 'low';
}
/** Confidence of one source→target pair; null when the type tier excludes it. */
function rateFieldMatch(field, column) {
    const score = pairScore(field, column);
    return score > 0 ? scoreToConfidence(score) : null;
}
// =============================================================================
// Main
// =============================================================================
function autoMatchFields(responseFields, targetColumns, existingMappedColumnIds = new Set(), isMappingAllowed) {
    // ── Phase 1: compute type-compatible candidates per target ─────────────
    const candidatesByTarget = new Map();
    for (const col of targetColumns) {
        const cands = [];
        for (const field of responseFields) {
            if (isMappingAllowed && !isMappingAllowed(field, col))
                continue;
            const score = pairScore(field, col);
            if (score > 0) {
                cands.push({
                    responseField: field.path,
                    responseFieldName: field.displayName,
                    score,
                });
            }
        }
        cands.sort((a, b) => b.score - a.score);
        if (cands.length > 0)
            candidatesByTarget.set(col.id, cands);
    }
    // ── Phase 2: greedy best-score-first assignment ────────────────────────
    // Auto-assign only medium+ (≥ 0.4). Low-confidence matches stay
    // available as dropdown alternatives but aren't pre-selected.
    const assignMinScore = 0.4;
    const allPairs = [];
    for (const [targetId, cands] of candidatesByTarget) {
        for (const c of cands)
            allPairs.push({ targetId, ...c });
    }
    allPairs.sort((a, b) => b.score - a.score);
    const claimedSources = new Set();
    const claimedTargets = new Set();
    const assignedSource = new Map();
    for (const pair of allPairs) {
        if (claimedSources.has(pair.responseField) ||
            claimedTargets.has(pair.targetId))
            continue;
        if (pair.score < assignMinScore)
            continue;
        claimedSources.add(pair.responseField);
        claimedTargets.add(pair.targetId);
        assignedSource.set(pair.targetId, pair.responseField);
    }
    // Phase 2b: for unclaimed targets, try best unclaimed source
    for (const col of targetColumns) {
        if (claimedTargets.has(col.id))
            continue;
        const cands = candidatesByTarget.get(col.id);
        if (!cands)
            continue;
        for (const c of cands) {
            if (claimedSources.has(c.responseField))
                continue;
            if (c.score < assignMinScore)
                continue;
            claimedSources.add(c.responseField);
            claimedTargets.add(col.id);
            assignedSource.set(col.id, c.responseField);
            break;
        }
    }
    // ── Phase 3: build suggestions with alternatives ───────────────────────
    const targetMap = new Map(targetColumns.map(c => [c.id, c]));
    const suggestions = [];
    for (const [targetId, responseField] of assignedSource) {
        const col = targetMap.get(targetId);
        const cands = candidatesByTarget.get(targetId) ?? [];
        const selected = cands.find(c => c.responseField === responseField);
        if (!selected)
            continue;
        suggestions.push({
            responseField: selected.responseField,
            responseFieldName: selected.responseFieldName,
            targetColumnId: col.id,
            targetColumnName: col.name,
            confidence: scoreToConfidence(selected.score),
            isOverwrite: existingMappedColumnIds.has(col.id),
            alternatives: cands.map(c => ({
                responseField: c.responseField,
                responseFieldName: c.responseFieldName,
                score: c.score,
                confidence: scoreToConfidence(c.score),
            })),
        });
    }
    const order = {
        high: 0,
        medium: 1,
        low: 2,
    };
    suggestions.sort((a, b) => {
        const co = order[a.confidence] - order[b.confidence];
        if (co !== 0)
            return co;
        return a.targetColumnName.localeCompare(b.targetColumnName);
    });
    return suggestions;
}
/**
 * How well a table fits another one: by its name and by how its fields find
 * their columns. Rates one pair; the caller ranks the pairs.
 */
function rateTableMatch(source, target) {
    const suggestions = autoMatchFields(source.fields, target.columns);
    const assignedScore = suggestions.reduce((sum, suggestion) => sum +
        (suggestion.alternatives.find(candidate => candidate.responseField === suggestion.responseField)?.score ?? 0), 0);
    const name = nameScore(source.name, target.name);
    const fields = source.fields.length === 0 ? 0 : assignedScore / source.fields.length;
    // Sure means: high confidence and the column takes the field's type as it is.
    const typeOf = new Map(source.fields.map(field => [field.path, field.type]));
    const columnTypeOf = new Map(target.columns.map(column => [column.id, column.type]));
    const sameType = (suggestion) => {
        const fieldType = typeOf.get(suggestion.responseField);
        const columnType = columnTypeOf.get(suggestion.targetColumnId);
        return (fieldType !== undefined &&
            columnType !== undefined &&
            autoMapCastTier(fieldType, columnType) === 'compatible');
    };
    const everyFieldSure = source.fields.length > 0 &&
        suggestions.filter(suggestion => suggestion.confidence === 'high' && sameType(suggestion)).length === source.fields.length;
    const percentOf = (score) => Math.floor(score * 100 + 1e-9);
    return {
        percent: name === 1 && everyFieldSure
            ? 100
            : Math.min(percentOf((name + fields) / 2), 99),
        namePercent: percentOf(name),
        fieldPercent: percentOf(fields),
        suggestions,
    };
}
